import "../Overview/EndpointOverview.css";
import "../Overview/EndpointDataSections.css";
import { createPortal } from "react-dom";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import CardPagination, { ENDPOINT_LOGS_PER_PAGE } from "../CardPagination";
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { OverviewDetailsProps } from '../Overview/interfaces/interface';
import TenantLists from '../TenantLists';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Button } from '../../ui/button';
import {
  Download,
  FileTerminal,
  FileText,
  Monitor,
  RefreshCw,
  Search,
  User,
  ChevronDown,
  ChevronRight,
  CalendarIcon,
} from 'lucide-react';
import { Input } from '../../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { format } from 'date-fns';
import { cn } from '../../../utils/utils';
import { Card, CardContent } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { myLocalStorage } from '../../StorageHelper';
import { fetchAuditLogsApi } from '../Lolbin/Api/lolbinapi';
import useUserStore from '../../../services/userStore';
import { Badge } from '../../ui/badge';
import { Skeleton } from '../../ui/skeleton';
import { handleGeneratePDF } from '../DownloadGenerate';
import CloudEntityActivityLogs from '../CloudAnalytics/CloudEntityActivityLogs';
import DownloadReportDialog from '../Alerts/DownloadReportDialog';
import EmptyStateCard from '../EmptyStateCard';
import {
  LIST_STATE_KEYS,
  parsePersistedDate,
  parsePersistedNumber,
  parsePersistedString,
  readPersistedListState,
  scopedListStateKey,
  SCOPED_LIST_STATE_PREFIX,
  serializePersistedDate,
  writePersistedListState,
} from '../../../utils/listPaginationState';

export default function AuditLogsDetails({
  onSelectorChange,
  fromdetails = false,
  computerName = '',
  computeremail = '',
  computerId = '',
  controlsContainer
}: OverviewDetailsProps) {
  const auditLogsStateKey = fromdetails
    ? scopedListStateKey(SCOPED_LIST_STATE_PREFIX.auditLogsDevice, computerId || computerName || 'unknown')
    : LIST_STATE_KEYS.auditLogs;
  const savedAuditState = readPersistedListState(auditLogsStateKey);

  const [selectedTime, setSelectedTime] = useState<string>(parsePersistedString(savedAuditState.selectedTime, 'lw'));
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(parsePersistedDate(savedAuditState.customStartDate));
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(parsePersistedDate(savedAuditState.customEndDate));
  const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedAuditState.searchQuery));
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(parsePersistedString(savedAuditState.appliedSearchQuery));
  const [isLoading, setIsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState<number>(fromdetails ? 1 : parsePersistedNumber(savedAuditState.page, 1));
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  // Download report dialog state
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [includeAlertJson, setIncludeAlertJson] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Handler for confirming download
  const handleDownloadReport = () => {
    setIsDownloading(true);
    const logsForReport = auditLogs.map(log => {
      if (includeAlertJson) {
        let eventJson = log?.eventJson;
        if (typeof eventJson === 'string') {
          try {
            eventJson = JSON.parse(eventJson);
          } catch {
            // leave as string
          }
        }
        return { ...log, alertJson: eventJson };
      } else {
        return { ...log };
      }
    });
    handleGeneratePDF(
      logsForReport,
      [null, null],
      searchQuery
    );
    setIsDownloading(false);
    setDownloadDialogOpen(false);
  };

  const tableRef = useRef<HTMLDivElement | null>(null);
  const skipAuditFilterResetRef = useRef(true);

  const tenantName = myLocalStorage.getItem('latestTenant')?.tenantName || '';
  const { email } = useUserStore((state) => state.user);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (!value.trim() && appliedSearchQuery !== '') {
      setAppliedSearchQuery('');
      setHasMore(true);
      setPage(1);
      fetchAuditLogs(true, '');
    }
  };
  const handleSearchClick = () => {
    const query = searchQuery.trim();
    setAppliedSearchQuery(query);
    setHasMore(true);
    setPage(1);
    fetchAuditLogs(true, query);
  };

  const toggleRowExpansion = (rowKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const getLogTypePriority = (type: string) => {
    if (type.toLowerCase().includes('blocked')) {
      return 'high';
    }

    switch (type) {
      case 'Enforce':
      case 'Alert':
      case 'AppInstall':
        return 'high';
      case 'LOLBin Audit':
      case 'Resource Control':
        return 'medium';
      case 'Audit':
      default:
        return 'low';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };


  const getBadgeClassByPriority = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-red-500 text-red-600 dark:border-red-400 dark:text-red-400';
      case 'medium':
        return 'border-yellow-500 text-yellow-600 dark:border-yellow-400 dark:text-yellow-400';
      case 'low':
      default:
        return 'border-green-500 text-green-600 dark:border-green-400 dark:text-green-400';
    }
  };
  const currentFetchController = useRef<AbortController | null>(null);
  const currentRequestId = useRef(0);
  const pageRef = useRef(1);

  const dedupeAuditLogs = (logs: any[]) => {
    const seen = new Set<string>();
    return logs.filter((log) => {
      const key = `${log?._time ?? ""}|${log?.resource ?? ""}|${log?.user ?? log?.userName ?? ""}|${log?.computer ?? log?.computerName ?? ""}|${log?.type ?? log?.resourceType ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchAuditLogs = async (reset = false, searchText = appliedSearchQuery) => {
    const bucket = 'ondevice-auditlog';
    const requestedPage = reset ? 1 : pageRef.current + 1;
    const requestId = ++currentRequestId.current;
    setIsLoading(true);
    if (currentFetchController.current) {
      currentFetchController.current.abort();
    }
    const controller = new AbortController();
    currentFetchController.current = controller;

    // Determine if custom mode is active
    const customMode = customStartDate && customEndDate;

    try {
      const response = await fetchAuditLogsApi(
        bucket,
        tenantName,
        customMode ? 'custom' : selectedTime,
        computerName,
        computerId,
        computeremail,
        searchText,
        email,
        fromdetails,
        "",
        "",
        requestedPage,
        200,
        controller.signal,
        customMode ? format(customStartDate, 'yyyy-MM-dd') : undefined,
        customMode ? format(customEndDate, 'yyyy-MM-dd') : undefined
      );

      if (requestId !== currentRequestId.current) return null;

      const logs = Array.isArray(response) ? response : [];

      // If API returns no rows, stop pagination and don't append
      if (logs.length === 0) {
        if (reset) {
          setAuditLogs([]);
          setPage(1);
        }
        setHasMore(false);
        return [];
      }

      const sortedLogs = [...logs].sort(
        (a, b) => new Date(b._time).getTime() - new Date(a._time).getTime()
      );

      setAuditLogs((prevLogs) =>
        reset ? sortedLogs : dedupeAuditLogs([...prevLogs, ...sortedLogs])
      );

      pageRef.current = requestedPage;
      if (reset) {
        setPage((currentPage) => Math.min(currentPage, Math.max(1, Math.ceil(sortedLogs.length / ENDPOINT_LOGS_PER_PAGE))));
      }

      return sortedLogs;
    } catch (error: any) {
      if (requestId !== currentRequestId.current) return null;
      // Don't clear data if request was aborted (user is typing fast)
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Request aborted - user changed search criteria');
      } else {
        console.error('Error fetching audit logs:', error);
        setHasMore(false);
      }
      return null;
    } finally {
      if (requestId === currentRequestId.current) {
        setIsLoading(false);
      }
    }
  };

  const handleAuditPageChange = async (nextPage: number) => {
    if (isLoading || nextPage < 1) return;

    let logCount = auditLogs.length;
    if (hasMore && nextPage * ENDPOINT_LOGS_PER_PAGE > logCount) {
      const logs = await fetchAuditLogs();
      if (logs === null) return;
      logCount = dedupeAuditLogs([...auditLogs, ...logs]).length;
    }

    setPage(Math.min(nextPage, Math.max(1, Math.ceil(logCount / ENDPOINT_LOGS_PER_PAGE))));
    tableRef.current?.scrollTo({ top: 0 });
  };

  useEffect(() => {
    writePersistedListState(auditLogsStateKey, {
      page,
      searchQuery,
      appliedSearchQuery,
      selectedTime,
      customStartDate: serializePersistedDate(customStartDate),
      customEndDate: serializePersistedDate(customEndDate),
    });
  }, [auditLogsStateKey, page, searchQuery, appliedSearchQuery, selectedTime, customStartDate, customEndDate]);

  useEffect(() => {
    if (!email || !tenantName) {
      return;
    }
    const shouldResetPage = !skipAuditFilterResetRef.current;
    skipAuditFilterResetRef.current = false;
    setHasMore(true);
    if (shouldResetPage) {
      setPage(1);
      pageRef.current = 1;
    }
    fetchAuditLogs(true, appliedSearchQuery);
  }, [selectedTime, appliedSearchQuery, email, tenantName, customStartDate, customEndDate]);

  const totalPages = Math.max(1, Math.ceil(auditLogs.length / ENDPOINT_LOGS_PER_PAGE));
  const visibleAuditLogs = auditLogs.slice((page - 1) * ENDPOINT_LOGS_PER_PAGE, page * ENDPOINT_LOGS_PER_PAGE);

  const timeRangeControls = (
    <>
      <div className="endpoint-filter-field flex items-center gap-2">
        <span className="endpoint-filter-label"><CalendarIcon />Time range</span>
        <Select value={selectedTime} onValueChange={(value) => {
          setSelectedTime(value);
          if (value !== 'custom') {
            setCustomStartDate(undefined);
            setCustomEndDate(undefined);
          }
        }}>
          <SelectTrigger className="endpoint-audit-time-range w-40" aria-label="Audit log time range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
            <SelectItem value="lw">Last 7 Days</SelectItem>
            <SelectItem value="lm">Last 30 Days</SelectItem>
            <SelectItem value="l3m">Last 90 Days</SelectItem>
            <SelectItem value="custom">Custom Date Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selectedTime === 'custom' && (
        <>
          <div className="endpoint-filter-field">
            <span className="endpoint-filter-label"><CalendarIcon />Start date</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-40 justify-start text-left font-normal",
                    !customStartDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {customStartDate ? format(customStartDate, "MMM dd") : "Start date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={customStartDate}
                  onSelect={(date) => {
                    setCustomStartDate(date);
                    if (customEndDate && date && customEndDate <= date) {
                      setCustomEndDate(undefined);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="endpoint-filter-field">
            <span className="endpoint-filter-label"><CalendarIcon />End date</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-40 justify-start text-left font-normal",
                    !customEndDate && "text-muted-foreground"
                  )}
                  disabled={!customStartDate}
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {customEndDate ? format(customEndDate, "MMM dd") : "End date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={customEndDate}
                  onSelect={setCustomEndDate}
                  initialFocus
                  disabled={(date) => {
                    if (!customStartDate) return true;
                    return date <= customStartDate;
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}
    </>
  );

  const refreshControl = (
    <Button
      variant="outline"
      size="sm"
      className="endpoint-time-refresh gap-2"
      onClick={() => {
        setAuditLogs([]);
        setHasMore(true);
        setPage(1);
        pageRef.current = 1;
        void fetchAuditLogs(true);
      }}
    >
      <RefreshCw className="w-4 h-4" />
      Refresh
    </Button>
  );

  return (
    <div className={cn("endpoint-overview endpoint-data-sections endpoint-audit-logs", !fromdetails && "endpoint-audit-page")}>
      {controlsContainer && createPortal(
        <div className="flex flex-wrap items-end gap-3">
          {timeRangeControls}
          {refreshControl}
        </div>,
        controlsContainer
      )}
      <div className="overview-card endpoint-section-toolbar">

        {/* Filters and Search */}
        <div className="endpoint-toolbar-fields flex items-center gap-4">
          {!fromdetails && <TenantLists onSelectorChange={onSelectorChange} isLabelNeeded={true} variant="card" />}

          {!controlsContainer && timeRangeControls}

          <div className="endpoint-filter-field endpoint-filter-search flex items-center gap-2">
            <span className="endpoint-filter-label"><Search />Search</span>
            <div className="relative">

              <Input
                aria-label="Search audit logs"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchClick();
                  }
                }}
                className="w-64 pr-10"
                data-testid="input-search-audit"
              />
              <button
                type="button"
                onClick={handleSearchClick}
                className="absolute transform -translate-y-1/2 right-3 top-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Search"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="endpoint-toolbar-actions flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setDownloadDialogOpen(true)}>
            <Download className="w-4 h-4" />
            Download Report
          </Button>

          {!controlsContainer && refreshControl}
        </div>

        <DownloadReportDialog
          open={downloadDialogOpen}
          onOpenChange={setDownloadDialogOpen}
          onConfirm={handleDownloadReport}
          onCancel={() => setDownloadDialogOpen(false)}
          loading={isDownloading}
          includeAlertJson={includeAlertJson}
          setIncludeAlertJson={setIncludeAlertJson}
          title="Do you want to download the Audit logs report?"
          subtitle="Include event JSON in PDF"
        />
      </div>

      {/* Audit Logs Table */}
      <Card className="overview-card endpoint-section-table-card">
        <OverviewCardHeader
          fromdetails
          title="Audit Logs"
          description={fromdetails ? "Recorded endpoint activity and event details" : "User activity logs from endpoints and servers"}
          icon={FileTerminal}
          detail={!isLoading ? `${auditLogs.length.toLocaleString()} loaded` : undefined}
        />
        <CardContent className="overview-card-content endpoint-table-content">
          {!isLoading && auditLogs.length === 0 ? (
            <div className="p-6">
              <EmptyStateCard
                icon={FileText}
                embedded
                title={appliedSearchQuery ? 'No audit logs match your search' : 'No audit logs found'}
                description={
                  appliedSearchQuery
                    ? 'Try adjusting your search terms or time range.'
                    : 'No activity was recorded for the selected time period.'
                }
              />
            </div>
          ) : (
          <div
            ref={tableRef}
            className={fromdetails ? "endpoint-audit-scroll overflow-x-auto" : "endpoint-audit-scroll overflow-auto"}
          >
            <Table className="endpoint-data-table w-full table-fixed">
              <TableHeader className={fromdetails ? undefined : "sticky top-0 z-10"}>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[5%] font-semibold text-foreground"></TableHead>
                  <TableHead className="w-[10%] font-semibold text-foreground">Timestamp</TableHead>
                  <TableHead className="w-[15%] font-semibold text-foreground">User</TableHead>
                  <TableHead className="w-[15%] font-semibold text-foreground">Computer</TableHead>
                  <TableHead className="w-[10%] font-semibold text-foreground">Event Type</TableHead>
                  <TableHead className="w-[45%] font-semibold text-foreground">Resource</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(isLoading ? [] : visibleAuditLogs).map((log, index) => {
                  const date = formatTimestamp(log?._time);
                  const priority = getLogTypePriority(log?.resourceType || log?.type || '');
                  const isHighPriority = priority === 'high';
                  const rowKey = `${log?._time ?? index}-${log?.resource ?? ""}-${log?.user ?? log?.userName ?? ""}-${log?.computer ?? log?.computerName ?? ""}`;
                  const isExpanded = expandedRows[rowKey];

                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow
                        onClick={() => toggleRowExpansion(rowKey)}
                        className={`endpoint-audit-row border-b border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-all duration-150 ${index % 2 === 0
                          ? 'bg-white dark:bg-gray-900'
                          : 'bg-gray-50 dark:bg-gray-800/50'
                          } ${isHighPriority ? 'border-l-4 border-red-500 dark:border-red-400' : ''}`}
                      >
                        <TableCell className="endpoint-entry-toggle-cell">
                          <button
                            type="button"
                            aria-label={isExpanded ? "Collapse event details" : "Expand event details"}
                            aria-expanded={Boolean(isExpanded)}
                            className="endpoint-entry-toggle"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                            )}
                          </button>
                        </TableCell>

                        <TableCell>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{date}</span>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-start gap-2">
                            <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 mt-0.5">
                              <User className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex flex-col min-w-0 gap-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                {log?.computerUserEmail?.split('\\').pop() || log?.user || '-'}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {log?.computerName?.includes('\\')
                                  ? log.computerName.split('\\')[0]
                                  : ''}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                              <Monitor className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {log?.computerName || log?.computer || '-'}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell align="center">
                          <Badge variant="outline" className={`px-2 text-sm font-medium break-words whitespace-normal ${getBadgeClassByPriority(priority)}`}
                          >
                            {log?.resourceType || log?.type || '-'}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-mono text-sm font-medium leading-relaxed break-all text-gray-900 dark:text-gray-100">
                              {log?.resourceName || log?.resource || '-'}
                            </span>
                            {log?.message && (
                              <div className="pl-3 mt-1 text-sm leading-relaxed border-l-2 border-gray-400 dark:border-gray-600 text-gray-600 dark:text-gray-400 break-words whitespace-pre-line block">
                                {log.message}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow
                          className={`${index % 2 === 0
                            ? 'bg-blue-50/50 dark:bg-gray-800'
                            : 'bg-blue-50/30 dark:bg-gray-900'
                            } border-b border-gray-200 dark:border-gray-700`}
                        >
                          <TableCell colSpan={6} className="p-4">
                            <div className="endpoint-event-expanded p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 rounded-lg border border-gray-200 dark:border-gray-700">
                              <h4 className="mb-3 text-sm font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                Event JSON
                              </h4>
                              {(() => {
                                let parsed = {};

                                try {
                                  parsed = log?.eventJson ? JSON.parse(log.eventJson) : {};
                                } catch (e) {
                                  parsed = {};
                                }

                                const isEmpty =
                                  !parsed ||
                                  (typeof parsed === "object" && Object.keys(parsed).length === 0);

                                return isEmpty ? (
                                  <span className="text-sm text-muted-foreground">No data available.</span>
                                ) : (
                                  <CloudEntityActivityLogs data={parsed} />
                                );
                              })()}

                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Loading Skeleton */}
                {isLoading &&
                  Array.from({ length: ENDPOINT_LOGS_PER_PAGE }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`} className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="w-full h-4 bg-gray-200 dark:bg-gray-700" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
          )}
        </CardContent>
        {auditLogs.length > 0 && (
          <div className="endpoint-table-pagination endpoint-pagination-bar border-t">
            <span className="endpoint-pagination-summary">
              Showing {(page - 1) * ENDPOINT_LOGS_PER_PAGE + 1}–{Math.min(page * ENDPOINT_LOGS_PER_PAGE, auditLogs.length)} of {auditLogs.length.toLocaleString()}{hasMore ? "+" : ""} entries
            </span>
            <CardPagination
              label="Audit log pages"
              currentPage={page}
              totalPages={totalPages}
              hasNextPage={page < totalPages || hasMore}
              disabled={isLoading}
              onPageChange={(nextPage) => { void handleAuditPageChange(nextPage); }}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
